import React from 'react';
import reuseIcon from '../images/analytics_icons/analytic_reuse_chair3.png';
import storedIcon from '../images/analytics_icons/analytic_assets_store5.png';
import thirdPartyIcon from '../images/analytics_icons/analytic_placement6.png';
import forThirdPartyIcon from '../images/analytics_icons/analytic_allocate7.png';
import recycledIcon from '../images/analytics_icons/analytic_recycled8.png';
import co2Icon from '../images/analytics_icons/analytic_buying10.png';
import weightIcon from '../images/analytics_icons/analytic_kg9.png';
import costIcon from '../images/analytics_icons/analytic_cost.png';
import AsyncSelect from 'react-select/async';
import LifecycleAnalytics from './LifecycleAnalytics';

class ReuseImpactDashboard extends React.Component {
  constructor(props) {
    super(props);
    
    this.state = {
      loading: true,
      activeTab: 'impact',
      userName: props.userName || '',
      analytics: {
        reused: 0,
        stored_for_reuse: 0,
        for_third_party: 0,
        third_party: 0,
        recycled: 0,
        co2_saved: 0,
        weight_saved: 0,
        cost_saved: 0,
        total_items: 0
      },
      startDate: props.startDate || '',
      endDate: props.endDate || '',
      project: props.project || '',
      projects: []
    };
  }

  componentDidMount() {
    // Small delay to ensure DOM is ready before loading analytics
    setTimeout(() => {
      this.loadAnalytics();
    }, 100);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.startDate !== this.props.startDate || 
        prevProps.endDate !== this.props.endDate ||
        prevProps.project !== this.props.project) {
      this.setState({
        startDate: this.props.startDate || '',
        endDate: this.props.endDate || '',
        project: this.props.project || ''
      }, () => {
        this.loadAnalytics();
      });
    }
  }

  loadProjects = (inputValue) => {
    if (!inputValue) return Promise.resolve([]);
  
    return fetch(`/dashboard/project_search?q=${inputValue}`)
      .then(res => res.json());
  };

  loadAnalytics() {
    this.setState({ loading: true });
    
    const params = new URLSearchParams();
    if (this.state.startDate) params.append('start_date', this.state.startDate);
    if (this.state.endDate) params.append('end_date', this.state.endDate);
    if (this.state.project) params.append('project', this.state.project);

    fetch(`/dashboard/analytics?${params.toString()}`)
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          // Store previous values for animation
          const prevAnalytics = this.state.analytics;
          
          this.setState({ 
            analytics: data.analytics,
            loading: false 
          }, () => {
            // Animate counters after state update
            setTimeout(() => {
              this.animateAllCounters(prevAnalytics, data.analytics);
            }, 100);
          });
        } else {
          console.error('Error loading analytics:', data.error);
          this.setState({ loading: false });
        }
      })
      .catch(err => {
        console.error('Error loading analytics:', err);
        this.setState({ loading: false });
      });
  }

  handleDateChange(type, value) {
    this.setState({ [type]: value });
  }

  handleProjectChange = (selected) => {
    this.setState(
      { project: selected ? selected.value : '' },
      () => this.loadAnalytics()
    );
  };

  handleGenerateReport() {
    // Placeholder for generate report functionality
    alert('Generate Report functionality will be implemented here');
  }

  animateCounter(element, start, end, duration = 2000, isCurrency = false, unit = '') {
    if (!element) return;
    
    const startTime = performance.now();
    const isDecimal = end % 1 !== 0 || isCurrency;
    const hasUnit = unit && unit.trim() !== '';
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const current = start + (end - start) * easeOutQuart;
      
      if (isCurrency) {
        element.textContent = `£${Math.max(0, current).toFixed(2)}`;
      } else if (hasUnit) {
        // Always show unit for weight/CO2, whether decimal or integer
        const currentIsWhole = Math.abs(current % 1) < 0.001; // Check if current value is effectively a whole number
        const formattedValue = currentIsWhole ? Math.max(0, Math.floor(current)) : Math.max(0, current).toFixed(1);
        element.textContent = `${parseFloat(formattedValue).toLocaleString()} ${unit}`.trim();
      } else if (isDecimal) {
        const formattedValue = Math.max(0, current).toFixed(1);
        element.textContent = `${parseFloat(formattedValue).toLocaleString()} ${unit}`.trim();
      } else {
        element.textContent = Math.max(0, Math.floor(current)).toLocaleString();
      }
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Final value
        if (isCurrency) {
          element.textContent = `£${end.toFixed(2)}`;
        } else if (hasUnit) {
          // Always show unit for weight/CO2, whether decimal or integer
          const endIsWhole = end % 1 === 0;
          const formattedValue = endIsWhole ? Math.floor(end) : end.toFixed(1);
          element.textContent = `${parseFloat(formattedValue).toLocaleString()} ${unit}`.trim();
        } else if (isDecimal) {
          const formattedValue = end.toFixed(1);
          element.textContent = `${parseFloat(formattedValue).toLocaleString()} ${unit}`.trim();
        } else {
          element.textContent = Math.floor(end).toLocaleString();
        }
      }
    };
    
    requestAnimationFrame(animate);
  }

  animateAllCounters(prevAnalytics, newAnalytics) {
    const counters = [
      { id: 'reused-counter', value: newAnalytics.reused, prev: prevAnalytics?.reused || 0 },
      { id: 'stored-counter', value: newAnalytics.stored_for_reuse, prev: prevAnalytics?.stored_for_reuse || 0 },
      { id: 'for-third-party-counter', value: newAnalytics.for_third_party, prev: prevAnalytics?.for_third_party || 0 },
      { id: 'third-party-counter', value: newAnalytics.third_party, prev: prevAnalytics?.third_party || 0 },
      { id: 'recycled-counter', value: newAnalytics.recycled, prev: prevAnalytics?.recycled || 0 },
      { id: 'co2-counter', value: newAnalytics.co2_saved, prev: prevAnalytics?.co2_saved || 0, isDecimal: true, unit: 'KG' },
      { id: 'weight-counter', value: newAnalytics.weight_saved, prev: prevAnalytics?.weight_saved || 0, isDecimal: true, unit: 'KG' },
      { id: 'cost-counter', value: newAnalytics.cost_saved, prev: prevAnalytics?.cost_saved || 0, isCurrency: true }
    ];

    counters.forEach(({ id, value, prev, isDecimal = false, isCurrency = false, unit = '' }) => {
      const element = document.getElementById(id);
      if (element) {
        // Get current displayed value or use previous
        let currentValue = prev;
        if (element.textContent) {
          // Remove currency symbols, commas, and units (KG) to parse the number
          const text = element.textContent.replace(/[£,]/g, '').replace(unit, '').trim();
          const parsed = parseFloat(text);
          if (!isNaN(parsed)) {
            currentValue = parsed;
          }
        }
        this.animateCounter(element, currentValue, value, 2000, isCurrency, unit);
      }
    });
  }

  componentDidUpdate(prevProps, prevState) {
    // Animation is now handled in loadAnalytics callback
    // This ensures smooth animation on both initial load and updates
  }


  render() {
    const { analytics, loading, startDate, endDate, project, projects, activeTab } = this.state;
    const { userName } = this.state;
    const { lifecycleEvents } = this.props;

    // Icon mapping
    const icons = {
      reuse: reuseIcon,
      stored_for_reuse: storedIcon,
      third_party: thirdPartyIcon,
      for_third_party: forThirdPartyIcon,
      recycled: recycledIcon,
      co2: co2Icon,
      weight: weightIcon,
      cost: costIcon
    };

    return (
      <div className="reuse-impact-dashboard" style={{
        backgroundColor: '#f5f5f5',
        padding: '32px',
        marginTop: '32px',
        borderRadius: '8px'
      }}>

        <div style={{
          backgroundColor: '#ffffff',
          padding: '32px',
          borderRadius: '8px',
          marginBottom: '32px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
        }}>
          <h1 style={{
            fontSize: '1.8rem',
            fontWeight: '600',
            color: '#212B35',
            marginBottom: '12px'
          }}>
            Welcome back {userName},
          </h1>

          <p style={{
            fontSize: '0.87rem',
            color: '#4B5563',
            marginBottom: '8px'
          }}>
            <strong>SmartED-i™</strong> is your live circular economy engine — a platform that tracks equipment,
            fittings, furniture, components and materials end-to-end, giving you real-time visibility
            of reuse, storage, donation and recycling outcomes.
          </p>

          <p style={{
            fontSize: '0.87rem',
            color: '#4B5563',
            marginBottom: '8px'
          }}>
            Here you’ll see key sustainability and value-based metrics — from CO₂e saved and waste avoided,
            to cost avoided by using existing assets. It’s designed to make impact visible and decisions smarter.
          </p>

          <p style={{
            fontSize: '0.87rem',
            color: '#4B5563',
          }}>
            To get the most value from SmartED-i™, start auditing assets, build a clear picture of what you have,
            where it is and its condition — then use the modules to identify the best reuse outcomes.
          </p>
        </div>

        {/* Tab switcher — matches SmartSUSTAIN reports tabs */}
        <ul className="tabs tab-buttons -margin-bottom_x2 -padding-left_reset -padding-top_reset reuse-dashboard-tabs">
          {[{ id: 'impact', label: 'Impact Overview' }, { id: 'lifecycle', label: 'Lifecycle Analytics' }].map((tab) => (
            <li key={tab.id} className={`tab-item${activeTab === tab.id ? ' active' : ''}`}>
              <button
                type="button"
                className="tab-url tab-button"
                onClick={() => this.setState({ activeTab: tab.id })}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>

        {activeTab === 'lifecycle' && (
          <LifecycleAnalytics
            startDate={startDate}
            endDate={endDate}
            project={project}
            lifecycleEvents={lifecycleEvents || []}
            canGenerateReport={this.props.canGenerateReport}
            reportTemplates={this.props.reportTemplates || []}
          />
        )}

        {activeTab === 'impact' && (
        <React.Fragment>
        {/* Date Filter Section */}
        <div style={{
          backgroundColor: '#fff',
          padding: '24px',
          borderRadius: '8px',
          marginBottom: '32px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            marginBottom: '8px',
            color: '#212B35'
          }}>
            Date Filter
          </h2>
          <p style={{
            fontSize: '0.9rem',
            color: '#637381',
            marginBottom: '24px'
          }}>
            Select a date range to filter data
          </p>
          
          <div style={{
            display: 'flex',
            gap: '16px',
            alignItems: 'flex-end',
            flexWrap: 'wrap'
          }}>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '0.9rem',
                fontWeight: '500',
                color: '#212B35'
              }}>
                From
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => this.handleDateChange('startDate', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    paddingRight: '40px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '0.9rem',
                    backgroundColor: '#fff'
                  }}
                />
                <span style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#637381',
                  pointerEvents: 'none'
                }}>
                  📅
                </span>
              </div>
            </div>

            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '0.9rem',
                fontWeight: '500',
                color: '#212B35'
              }}>
                To
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => this.handleDateChange('endDate', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    paddingRight: '40px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '0.9rem',
                    backgroundColor: '#fff'
                  }}
                />
                <span style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#637381',
                  pointerEvents: 'none'
                }}>
                  📅
                </span>
              </div>
            </div>

            <div style={{ flex: '1', minWidth: '220px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '0.9rem',
                fontWeight: '500',
                color: '#212B35'
              }}>
                Project
              </label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={this.loadProjects}
                onChange={this.handleProjectChange}
                placeholder="Search project…"
                isClearable
                value={project ? { value: project, label: project } : null}
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '42px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '0.9rem'
                  })
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => this.loadAnalytics()}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#3C93C9',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#2d7ba8'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#3C93C9'}
              >
                Filter
              </button>
              {/* <a
                href="/reports"
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#8484bf',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'background-color 0.2s',
                  textDecoration: 'none',
                  display: 'inline-block'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#6d6da8'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#8484bf'}
              >
                SmartSUSTAIN™
              </a> */}
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', backgroundColor: '#fff', borderRadius: '8px' }}>
            <div className="spinner" style={{
              border: '4px solid #f3f4f6',
              borderTop: '4px solid #3C93C9',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }}></div>
            <p style={{ marginTop: '16px', color: '#637381' }}>Loading analytics...</p>
          </div>
        ) : (
          <div className="metrics-grid">
            <MetricCard
              title="Reused"
              value={analytics.reused}
              id="reused-counter"
              color="#2F8D37"
              iconUrl={icons.reuse}
            />
            <MetricCard
              title="Stored for Reuse"
              value={analytics.stored_for_reuse}
              id="stored-counter"
              color="#D3398D"
              iconUrl={icons.stored_for_reuse}
            />
            <MetricCard
              title="For Third Party Placement"
              value={analytics.for_third_party}
              id="for-third-party-counter"
              color="#88CDD3"
              iconUrl={icons.for_third_party}
            />
            <MetricCard
              title="Third Party Placement"
              value={analytics.third_party}
              id="third-party-counter"
              color="#3C93C9"
              iconUrl={icons.third_party}
            />
            <MetricCard
              title="Recycled"
              value={analytics.recycled}
              id="recycled-counter"
              color="#FAB511"
              iconUrl={icons.recycled}
            />
            <SustainabilityCard
              title="CO₂e Saved"
              value={analytics.co2_saved}
              id="co2-counter"
              unit="KG"
              color="#2F8D37"
              iconUrl={icons.co2}
            />
            <SustainabilityCard
              title="Weight Saved"
              value={analytics.weight_saved}
              id="weight-counter"
              unit="KG"
              color="#3C93C9"
              iconUrl={icons.weight}
            />
            <SustainabilityCard
              title="Cost Saved"
              value={analytics.cost_saved}
              id="cost-counter"
              unit="£"
              color="#D3398D"
              iconUrl={icons.cost}
              isCurrency={true}
            />
          </div>
        )}
        </React.Fragment>
        )}

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .reuse-dashboard-tabs {
            border-bottom: 1px solid #dedede;
            width: 100%;
          }
          .reuse-dashboard-tabs .tab-item {
            list-style: none;
            display: inline-block;
          }
          .reuse-dashboard-tabs .tab-button {
            background: none;
            border: none;
            border-bottom: 2px solid transparent;
            cursor: pointer;
            font-family: inherit;
            height: 2.6rem;
            display: inline-block;
            padding: 10px 16px;
            border-radius: 0;
            text-transform: uppercase;
            text-decoration: none;
            color: #757575;
            font-size: 0.85rem;
            font-weight: 500;
            letter-spacing: 0.02em;
          }
          .reuse-dashboard-tabs .tab-item.active .tab-button,
          .reuse-dashboard-tabs .tab-button:hover {
            color: #393939;
            border-bottom-color: #12d2f2;
          }
          
          .metrics-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 24px;
          }
          
          @media (max-width: 1200px) {
            .metrics-grid {
              grid-template-columns: repeat(3, 1fr);
            }
          }
          
          @media (max-width: 768px) {
            .metrics-grid {
              grid-template-columns: repeat(2, 1fr);
            }
          }
          
          @media (max-width: 480px) {
            .metrics-grid {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
      </div>
    );
  }
}

const MetricCard = ({ title, value, id, color, iconUrl, showProgress = false, total = 0 }) => {
  return (
    <div style={{
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '32px',
      textAlign: 'center',
      boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
      transition: 'box-shadow 0.2s',
      cursor: 'default',
      minHeight: '200px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between'
    }}
    onMouseOver={(e) => e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.12)'}
    onMouseOut={(e) => e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08)'}
    >
      <div style={{
        marginBottom: '20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '80px'
      }}>
        <img src={iconUrl} alt={title} style={{ maxHeight: '80px', maxWidth: '120px', objectFit: 'contain' }} />
      </div>
      <h3 style={{
        fontSize: '1.1rem',
        fontWeight: '500',
        color: '#637381',
        marginBottom: '24px',
        textTransform: 'none',
        letterSpacing: '0.3px'
      }}>
        {title}
      </h3>
      <div
        id={id}
        style={{
          fontSize: '2.2rem',
          fontWeight: '700',
          color: '#212B35',
          lineHeight: '1.2'
        }}
      >
        0
      </div>
    </div>
  );
};

const SustainabilityCard = ({ title, value, id, unit, color, iconUrl, isCurrency = false }) => {
  const formatValue = (val) => {
    if (isCurrency) {
      return `£${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    // For weight/CO2, always show unit, format based on whether it's a whole number or decimal
    const isWholeNumber = val % 1 === 0;
    if (isWholeNumber) {
      return `${val.toLocaleString()} ${unit}`;
    }
    return `${val.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ${unit}`;
  };

  // Get icon based on title
  // const getIcon = () => {
  //   if (title.includes('Cost')) return '💰';
  //   return '📊';
  // };

  return (
    <div style={{
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '32px',
      textAlign: 'center',
      boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
      transition: 'box-shadow 0.2s',
      cursor: 'default',
      minHeight: '200px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between'
    }}
    onMouseOver={(e) => e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.12)'}
    onMouseOut={(e) => e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08)'}
    >
      <div style={{
        fontSize: '3.5rem',
        marginBottom: '20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '80px'
      }}>
        <img src={iconUrl} alt={title} style={{ maxHeight: '80px', maxWidth: '120px', objectFit: 'contain' }} />
      </div>
      <h3 style={{
        fontSize: '1.1rem',
        fontWeight: '500',
        color: '#637381',
        marginBottom: '24px',
        textTransform: 'none',
        letterSpacing: '0.3px',
        lineHeight: '1.4'
      }}>
        {title}
      </h3>
      <div
        id={id}
        style={{
          fontSize: '2.2rem',
          fontWeight: '700',
          color: '#212B35',
          lineHeight: '1.2'
        }}
      >
        {isCurrency ? '£0.00' : `0 ${unit}`.trim()}
      </div>
    </div>
  );
};

export default ReuseImpactDashboard;

